import React, { useState } from "react";
import { ArrowDown, Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import moment from "moment";

const SkeletonBlock = ({ className = "" }) => (
  <div className={`animate-pulse bg-slate-200 rounded ${className}`} />
);

type TransferStatus = "created" | "inTransit" | "delivered";

type ASNTransfer = {
  asn: string;
  qty: number;
  status?: TransferStatus;
  dateTime: string;
  transferredFromSite?: string;
  transferredFromZone?: string;
  transferredToSite?: string;
  transferredToZone?: string;
};

type ResponsivePaginatedTransfersListProps = {
  pagedRows: ASNTransfer[];
  currentAsn?: string;
  setSelectedAsn: React.Dispatch<React.SetStateAction<ASNTransfer | null>>;
  loading?: boolean;
};

export default function ResponsivePaginatedTransfersList({
  pagedRows = [],
  currentAsn,
  setSelectedAsn,
  loading = false,
}: ResponsivePaginatedTransfersListProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  const totalItems = pagedRows?.length || 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentVisibleRows = pagedRows.slice(indexOfFirstItem, indexOfLastItem);

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  if (loading) {
    return (
      <div className="w-full md:w-80 lg:w-96 flex-shrink-0 flex flex-col bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden h-full max-h-[calc(100vh-2rem)]">
        {/* Header Skeleton */}
        <div className="p-3.5 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
          <SkeletonBlock className="w-28 h-4" />
          <SkeletonBlock className="w-16 h-5 rounded-full" />
        </div>

        {/* Cards Skeleton */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
          {[...Array(6)].map((_, index) => (
            <div
              key={index}
              className="w-full p-3.5 border border-slate-200 rounded-xl bg-white space-y-3"
            >
              <div className="flex justify-between items-center">
                <div className="space-y-2">
                  <SkeletonBlock className="w-10 h-2" />
                  <SkeletonBlock className="w-28 h-4" />
                </div>
                <SkeletonBlock className="w-14 h-6 rounded-md" />
              </div>
              <div className="rounded-lg p-3 border border-slate-100 bg-slate-50 space-y-3">
                <SkeletonBlock className="w-full h-4" />
                <SkeletonBlock className="w-4 h-4" />
                <SkeletonBlock className="w-full h-4" />
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                <SkeletonBlock className="w-4 h-4 rounded-full" />
                <SkeletonBlock className="w-32 h-3" />
              </div>
            </div>
          ))}
        </div>

        {/* Pagination Skeleton */}
        <div className="p-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
          <SkeletonBlock className="w-32 h-4" />
          <div className="flex gap-2">
            <SkeletonBlock className="w-8 h-8 rounded-lg" />
            <SkeletonBlock className="w-14 h-8 rounded-lg" />
            <SkeletonBlock className="w-8 h-8 rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  const statusStyles: Record<TransferStatus, string> = {
    created: "bg-blue-50 text-blue-700 border-blue-200",
    inTransit: "bg-amber-50 text-amber-700 border-amber-200",
    delivered: "bg-green-50 text-green-700 border-green-200",
  };

  const statusLabel: Record<TransferStatus, string> = {
    created: "Created",
    inTransit: "In Transit",
    delivered: "Delivered",
  };

  return (
    <div className="w-full md:w-80 lg:w-96 flex-shrink-0 flex flex-col bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden h-full max-h-[calc(100vh-2rem)]">
      {/* Header */}
      <div className="p-3.5 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
          Transfers List
        </h3>
        <span className="text-[10px] bg-slate-200/60 text-slate-600 px-2 py-0.5 rounded-full font-medium">
          {totalItems} Total
        </span>
      </div>

      {/* Cards */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
        {currentVisibleRows.length > 0 ? (
          currentVisibleRows.map((item) => {
            const isActive = currentAsn === item.asn;

            return (
              <div
                key={item.asn}
                onClick={() => setSelectedAsn(item)}
                className={`group relative w-full flex flex-col justify-between px-3.5 py-2.5 border rounded-xl transition-all duration-300 ease-in-out cursor-pointer overflow-hidden ${
                  isActive
                    ? "border-[var(--primaryLight)] bg-[var(--primaryExtraLight)] shadow-sm pl-13"
                    : "bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50/30 pl-3.5"
                }`}
              >
                {/* Active Indicator & Action Circle */}
                {isActive && (
                  <>
                    {/* Left Accent Accent Line */}
                    <div className="absolute top-0 left-0 bottom-0 w-1 bg-[var(--actionOrange)]" />

                    {/* Professional Inner-Left Circular Arrow */}
                    <div className="absolute right-6 top-1/2 -translate-y-1/2 flex items-center justify-center w-7 h-7 rounded-full bg-[var(--primary)] text-white shadow-sm transition-all duration-300 scale-100 animate-in fade-in zoom-in-75 duration-200">
                      <ChevronRight className="w-4 h-4 stroke-[3]" />
                    </div>
                  </>
                )}

                {/* Top Header Block */}
                <div className="flex items-center justify-between gap-3 mb-2.5 min-h-[28px]">
                  {/* ASN */}
                  <div className="flex items-center min-w-0 flex-1">
                    <span
                      className={`font-mono text-xs font-bold truncate leading-none transition-colors ${
                        isActive
                          ? "text-[var(--primary)]"
                          : "text-slate-800 group-hover:text-[var(--primary)]"
                      }`}
                    >
                      {item.asn}
                    </span>
                  </div>

                  {/* Status Badge */}
                  <div className="flex items-center shrink-0">
                    <span
                      className={`inline-flex items-center justify-center gap-1.5 h-6 px-2 rounded-md text-[10px] font-semibold whitespace-nowrap shrink-0 border transition-colors ${
                        statusStyles[item.status!]
                      }`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70 shrink-0" />
                      {statusLabel[item.status!]}
                    </span>
                  </div>
                </div>

                {/* Route Visualizer Map Box */}
                <div
                  className={`rounded-lg p-2.5 space-y-1.5 border transition-colors ${
                    isActive
                      ? "bg-white border-[var(--primaryLight)]"
                      : "bg-slate-50/80 border-slate-100"
                  }`}
                >
                  <div className="truncate flex items-baseline text-sm">
                    <span className="text-[9px] font-bold text-slate-400 uppercase w-8 shrink-0">
                      From
                    </span>
                    <span className="font-semibold text-slate-700 truncate">
                      {item.transferredFromSite}
                      <span className="font-normal text-slate-400 text-[10px] ml-1">
                        ({item.transferredFromZone})
                      </span>
                    </span>
                  </div>

                  <div
                    className={`pl-8 -my-1 transition-colors ${
                      isActive ? "text-[var(--primary)]" : "text-slate-300"
                    }`}
                  >
                    <ArrowDown className="w-3 h-3" />
                  </div>

                  <div className="truncate flex items-baseline text-sm">
                    <span
                      className={`text-[9px] font-bold uppercase w-8 shrink-0 transition-colors ${
                        isActive ? "text-[var(--primary)]" : "text-slate-400"
                      }`}
                    >
                      To
                    </span>
                    <span className="font-semibold text-slate-800 truncate">
                      {item.transferredToSite}
                      <span className="font-normal text-slate-400 text-[10px] ml-1">
                        ({item.transferredToZone})
                      </span>
                    </span>
                  </div>
                </div>

                {/* Card Bottom Footer Metadata */}
                <div className="flex items-center justify-between w-full mt-2.5 pt-2 border-t border-slate-100/80">
                  <div className="flex items-center gap-1.5 min-w-0 text-[10px] text-slate-400">
                    <Calendar className="w-3 h-3 text-slate-400 shrink-0" />
                    <span className="truncate">
                      {moment(item.dateTime).format("DD MMM YYYY · hh:mm A")}
                    </span>
                  </div>

                  <span
                    className={`inline-flex items-center justify-center px-2 py-0.5 rounded-md text-[10px] font-bold shrink-0 transition-colors ${
                      isActive
                        ? "bg-[var(--primary)] text-white"
                        : "bg-slate-100 text-slate-700"
                    }`}
                  >
                    {item.qty} QTY
                  </span>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-8 text-xs font-medium text-slate-400">
            No active transfers loaded.
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      <div className="p-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between text-xs font-medium text-slate-600">
        <div className="text-[11px] text-slate-400">
          Showing{" "}
          <span className="font-bold text-slate-600">
            {totalItems === 0 ? 0 : indexOfFirstItem + 1}
          </span>{" "}
          to{" "}
          <span className="font-bold text-slate-600">
            {Math.min(indexOfLastItem, totalItems)}
          </span>{" "}
          of <span className="font-bold text-slate-600">{totalItems}</span>
        </div>

        <div className="flex items-center gap-1.5">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className="p-1.5 bg-white border border-slate-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-3.5 h-3.5 text-slate-500" />
          </button>

          <span className="text-[11px] px-2 text-slate-500 font-mono">
            {currentPage} / {totalPages}
          </span>

          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="p-1.5 bg-white border border-slate-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
          </button>
        </div>
      </div>
    </div>
  );
}
